import { useUpdateTemplateMutation } from '@/services/contractTemplates';
import { Button, Modal, toast } from '@palmetto/palmetto-components';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';

import { ContractDocumentProviders, Locale, OrganizationContractDocumentSettings } from 'types';

import { isErrorWithData } from '../../services/helpers';

import { ContractTemplateFormFields } from './ContractTemplateFormFields';
import {
  contractTemplateSchema,
  ContractTemplateFormValues,
  toContractTemplatePayload,
  VaultChoice,
} from './contractTemplateSchema';

const toVaultChoice = (vaultOnCompletion: boolean | undefined): VaultChoice => {
  if (vaultOnCompletion === undefined) return '';
  return vaultOnCompletion ? 'yes' : 'no';
};

export const getTemplateId = (
  template: OrganizationContractDocumentSettings | null,
  language: Locale,
  coborrower: boolean,
): string | undefined =>
  template?.contractSettings?.contractDocumentSettings?.templates?.find(
    (t) => t.language === language && t.coborrower === coborrower,
  )?.templateId;

export const EditContractModal = ({
  isOpen,
  handleClose,
  template,
}: {
  isOpen: boolean;
  handleClose: () => void;
  template: OrganizationContractDocumentSettings | null;
}) => {
  const [updateTemplate] = useUpdateTemplateMutation();

  const handleSubmit = async (values: ContractTemplateFormValues) => {
    try {
      await updateTemplate({
        organizationSettingsId: template?.organizationSettingsId?.toString() ?? '',
        contractDocumentSettingsId: template?.contractSettings.contractDocumentSettingsId?.toString() ?? '',
        templateData: toContractTemplatePayload(values),
      }).unwrap();
      toast.success('Contract template updated');
      handleClose();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error('Error updating contract template');
      }
    }
  };

  const initialValues: ContractTemplateFormValues = useMemo(() => {
    const documentSettings = template?.contractSettings?.contractDocumentSettings;
    return {
      programType: template?.programType ?? '',
      state: template?.contractSettings?.state ?? '',
      lseId: template?.contractSettings?.lseId ?? '',
      contractType: template?.contractSettings.type,
      provider:
        documentSettings?.provider === ContractDocumentProviders.conga
          ? ContractDocumentProviders.conga
          : ContractDocumentProviders.docusign,
      requiredAdditionalSigners: documentSettings?.requiredAdditionalSigners ?? [],
      congaSpanishAvailable: documentSettings?.congaSpanishAvailable ?? false,
      vaultOnCompletion: toVaultChoice(documentSettings?.vaultOnCompletion),
      eOriginalLender: documentSettings?.eOriginalLender ?? '',
      englishTemplateId: getTemplateId(template, Locale.en, false) ?? '',
      englishCosignTemplateId: getTemplateId(template, Locale.en, true) ?? '',
      spanishTemplateId: getTemplateId(template, Locale.es, false) ?? '',
      spanishCosignTemplateId: getTemplateId(template, Locale.es, true) ?? '',
    };
  }, [template]);

  if (!template || !isOpen) {
    return null;
  }

  const templateKey = template?.contractSettings?.contractDocumentSettingsId?.toString() ?? 'new';

  return (
    <Formik
      key={templateKey}
      validationSchema={contractTemplateSchema}
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, isValidating, submitForm, errors }) => (
        <Form noValidate>
          <Modal isOpen={true} maxWidth="4xl" ariaLabelledBy="editContractHeader" onDismiss={handleClose}>
            <Modal.Header id="editContractHeader" title="Edit Contract" onDismiss={handleClose} />
            <Modal.Body background="secondary" childGap="lg">
              <ContractTemplateFormFields />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting || isValidating || Object.keys(errors).length > 0}
                type="submit"
                onClick={submitForm}
              >
                Edit Contract
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
